import * as React from 'react'
import { cn } from '@/lib/utils'

interface SegmentedControlProps {
  value: string
  onValueChange: (value: string) => void
  className?: string
  children: React.ReactNode
}

interface SegmentedControlItemProps {
  value: string
  icon?: React.ReactElement
  label: string
  count?: number
  className?: string
}

const SegmentedControlContext = React.createContext<{
  value: string
  onValueChange: (value: string) => void
} | null>(null)

function useSegmentedControl() {
  const context = React.useContext(SegmentedControlContext)
  if (!context) {
    throw new Error('SegmentedControlItem must be used within SegmentedControl')
  }
  return context
}

export function SegmentedControl({
  value,
  onValueChange,
  className,
  children,
}: SegmentedControlProps) {
  return (
    <SegmentedControlContext.Provider value={{ value, onValueChange }}>
      <div
        className={cn(
          'inline-flex h-9 items-baseline justify-start rounded-full border border-input bg-brand-100 p-1',
          className
        )}
        role="tablist"
      >
        {children}
      </div>
    </SegmentedControlContext.Provider>
  )
}

export function SegmentedControlItem({
  value,
  icon,
  label,
  count,
  className,
}: SegmentedControlItemProps) {
  const { value: selectedValue, onValueChange } = useSegmentedControl()
  const isActive = selectedValue === value

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={() => onValueChange(value)}
      className={cn(
        'group inline-flex items-center justify-center whitespace-nowrap py-2 align-middle font-semibold transition-all duration-300 ease-in-out disabled:cursor-not-allowed stroke-blue-700 min-w-[32px] text-xs disabled:stroke-slate-400 disabled:text-slate-400 hover:stroke-blue-950 hover:text-blue-950 h-7 px-8',
        isActive
          ? 'rounded-full bg-white text-gray-900 shadow-sm ring-1 ring-gray-900/5'
          : 'rounded-full bg-transparent text-gray-600 hover:bg-white/50 hover:text-gray-900',
        className
      )}
    >
      <div className="flex items-center gap-1">
        {icon && (
          <span className="size-4">
            {React.cloneElement(icon, {
              className: cn('size-4', icon.props.className),
            })}
          </span>
        )}
        <div>{label}</div>
        {count !== undefined && (
          <span className="inline-flex items-center justify-center rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
            {count}
          </span>
        )}
      </div>
    </button>
  )
}

SegmentedControl.Item = SegmentedControlItem
